import React, {useState, useRef, useCallback} from 'react';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import I18n from 'app/i18n';
import BonusInterfaces from 'app/interfaces/BonusInterface';
import {modalConfirm} from 'app/utils/modals/popups';
import {toastError} from 'app/components/toasts/liteToast';
import {getImageByBonusesType, getKeyByType, PARTS_IN_PROGRESS} from 'app/components/my_profile/bonuses/bonuses_utils';
import {useClickOutside} from 'app/utils/useClickOutside';
import BonusesCardTime from 'app/components/my_profile/bonuses/BonusesCardTime';

interface BonusCardProps extends BonusInterfaces {
    hasActiveBonus?: boolean
    activateBonus?: (id: number) => void
    hideExpiredBonus?: (id: number) => void
}

const BonusesCard: React.FC<BonusCardProps> = props => {
    const {score, score_all, sum, id, type, rows, time_left, birthday} = props;
    const [isInfoActive, setIsInfoActive] = useState(false);
    const bonusSum = Math.trunc(sum);
    const fraction = Math.trunc((sum - bonusSum) * 100);
    const cardRef = useRef<HTMLDivElement>(null);

    useClickOutside(cardRef, () => setIsInfoActive(false), isInfoActive);

    const toggleInfoActive = useCallback(() => setIsInfoActive(prev => !prev), []);

    const handleActivateBonus = useCallback(() => {
        const {activateBonus, hasActiveBonus} = props;

        if (hasActiveBonus) {
            toastError(I18n.t('bonuses_page.error_cancel_current_bonus'));
            return;
        }

        modalConfirm(`${I18n.t('activate_bonus_message')}`, () => {
            activateBonus(id);
        });
    }, [
        id,
        props.activateBonus,
        props.hasActiveBonus
    ]);

    const hideExpiredBonus = useCallback(() => {
        props.hideExpiredBonus?.(id);
    }, [id, props.hideExpiredBonus]);

    const bonusProgress = (bonusScore: number, bonusScoreAll: number) => Math.floor(bonusScore / bonusScoreAll * 100);

    const renderProgress = () => {
        const bonuses = rows?.length > 0 ? rows : [...Array(PARTS_IN_PROGRESS)];

        return <>
            {type.toLowerCase() === 'classic'
                ? <div className="bonuses-card__progress-wrapper bonuses-card__progress--classic">
                    {bonuses.map((bonus, index) => <div className="bonuses-card__progress-background" key={`bonus-progress-${id}-${index}` }>
                        <div
                            className="bonuses-card__progress-fill"
                            style={{width: `${bonusProgress(bonus?.score, bonus?.score_all)}%`}}
                        />
                    </div>)}
                </div>
                : <div className="bonuses-card__progress-background">
                    <div className="bonuses-card__progress-fill" style={{width: `${bonusProgress(score, score_all)}%`}}/>
                </div>
            }
        </>;
    };

    return <div className="bonuses-card" ref={cardRef}>
        {isInfoActive
            ? <div className="bonuses-card-wrapper-info">
                <p className="bonuses-card-wrapper-info__title">
                    {I18n.t(getKeyByType(type, birthday))}
                </p>
                <div
                    className="bonuses-card-wrapper-info__text"
                    dangerouslySetInnerHTML={
                        {__html: I18n.t(`bonuses_page.${getKeyByType(type, birthday)}_desc`)}
                    }
                />
            </div>
            : <div className="bonuses-card-wrapper">
                <div className="bonuses-card__img">
                    <img src={getImageByBonusesType(type, birthday)} alt={I18n.t(getKeyByType(type, birthday))}/>
                </div>
                <p className="bonuses-card__amount">
                    ${bonusSum}
                    {fraction > 0 && <span className="bonuses-card__amount-fraction">.{fraction}</span>}
                </p>
                <div className="bonuses-card-turnover">
                    <p className="bonuses-card-turnover__title">
                        <span>{I18n.t('bonuses_page.turnover')}</span>
                        {type === 'Classic' && <span>5x${score_all / PARTS_IN_PROGRESS}</span>}
                    </p>
                    <div className="bonuses-card__progress" data-turnover={`$${score}`}>
                        {renderProgress()}
                    </div>
                </div>
                <BonusesCardTime timeLeft={time_left} hideExpiredBonus={hideExpiredBonus}/>
                <button
                    className="bonuses-card__btn bonuses-card__btn--activation"
                    onClick={handleActivateBonus}
                >
                    {I18n.t('activate')}
                </button>
            </div>}
        {isInfoActive
            ? <div className="bonuses-card__close" onClick={toggleInfoActive}>
                <SpriteIcon name="#svg-close" size={{height: 12, width: 12}}/>
            </div>
            : <div className="bonuses-card__info" onClick={toggleInfoActive}>
                <SpriteIcon name="#svg-question" size={{height: 16, width: 16}}/>
            </div>}
    </div>;
};

export default BonusesCard;
